// ============================================================================
// Turning push alerts on and off for THIS device (RIZ132): the subscribe flow,
// the device list, the test alert. The rules that need no browser (can this
// browser push at all, how a key is read, how a device is named) are in
// pushSupport.ts; the server half is mig 480 (push_subscriptions and
// save_push_subscription), the process-notification-push drain and push-alerts.
//
// A device is a person's, not a brokerage's: the same phone serves a person in
// every brokerage they belong to, so nothing here takes an organization.
//
// THE ORDER OF enableAlerts IS LOAD-BEARING.
//   1. Notification.requestPermission() comes BEFORE ANY OTHER AWAIT. iOS shows the
//      permission prompt only for a request made inside the person's tap, and a tap
//      stops counting once something else has been waited for ahead of the request.
//      That is also why the VAPID key is passed IN: the card fetches it when it
//      mounts (fetchVapidKey), never inside the click.
//   2. The session. save_push_subscription is granted to signed-in people only, so for
//      anyone else the save can only fail, and only after a subscription has been made
//      for nothing; a missing session is said here, in the person's own words.
//   3. serviceWorker.ready, but only as long as READY_TIMEOUT_MS (a page with no
//      worker would wait for ever).
//   4. The ping. The app's worker (vite-plugin-pwa, prompt mode) answers it only when
//      it has the push handlers in public/push-sw.js. The ACTIVE worker may be an
//      older one while a newer one waits for the person to accept the update, or one
//      whose push-sw.js failed to load (the import fails without failing the install,
//      and leaves an empty worker): a push to either shows nothing, and Chrome then
//      puts up its own "updated in the background" notice. So without a pong nothing
//      is subscribed and nothing is saved. It is asked twice (PING_ATTEMPTS): a worker
//      the browser has stopped is woken by the first and answers the second.
//   5. A subscription made with another key is dropped first (a rotated VAPID key
//      cannot be reused, and a second subscription with a different key is an error).
//   6. subscribe, userVisibleOnly, then the definer RPC.
//
// TURN OFF AND SIGN-OUT MUST SETTLE PROMPTLY. The sign-out awaits disableAlerts()
// before it ends the session (the delete needs the session), so no path of
// disableAlerts or forgetThisDevice waits on serviceWorker.ready: they ask for the
// registration that exists (getRegistration answers at once, with nothing if there
// is none). The browser is unsubscribed BEFORE the row is deleted: if the delete
// then hangs or fails, this device still gets nothing. And the delete, which is a
// request with no timeout of its own, is waited for only DELETE_TIMEOUT_MS: one that
// has not answered by then is let go and logged, not a failure, because the push
// service answers the next alert 410 for an unsubscribed device and the drain drops
// the row.
//
// NOTHING HANGS THE CARD BUT THE PERMISSION PROMPT (Task 8's review). The prompt is
// the person's to answer and is never timed. Every other call that a push service
// that never answers could leave waiting is bounded: unsubscribe() at
// UNSUBSCRIBE_TIMEOUT_MS (turning off, the signed-out event, and Turn on's drop of a
// subscription made with another key) and subscribe() at SUBSCRIBE_TIMEOUT_MS,
// counted from after the permission answer. An unsubscribe let go at its limit is a
// failed one: turning off still deletes the row, then says so. A limit that is hit
// leaves a cause naming the call and the limit, for the log.
//
// A DELETE THAT REMOVES 0 ROWS IS NOT A FAILURE. push_subscriptions carries the
// PII tables' "Require AAL2 when MFA enrolled" overlay (mig 480), so an MFA-enrolled
// member on a password-only session sees no devices and deletes none, and neither
// error nor count says so. Turn off, Remove and sign-out look at the error and at
// nothing else; asking for the removed rows to prove one went would turn that
// member's sign-out into a failure. Likewise an empty device list is a list.
//
// WHAT THE BROWSER AND THE NETWORK SAY NEVER REACHES A PERSON. A toast shows
// `error.message`, and the browser's own words ("Registration failed - push service
// error", "TypeError: Failed to fetch", Safari's "Load failed") mean nothing to a
// realtor. Every failure of a call into the browser's push API is a plain sentence
// in this file's voice with the browser's error kept as the `cause`; a supabase-js
// error with no code (the request never got an answer, or a gateway's page or text
// answered in the database's place) is "Couldn't reach Rize OS to <what was being
// done>"; and an error that has a code is the database's, said in its own words,
// with the error as the cause. A failed Turn off is one sentence, TURN_OFF_FAILED,
// whether the browser would not unsubscribe or the database gave no words. Nothing
// here puts a caught error's text into a sentence.
//
// The card dispatches PUSH_DEVICES_CHANGED_EVENT after Turn on, Turn off and Remove;
// usePushDevices reloads on it. Nothing in this file dispatches it.
// ============================================================================
import { edgeFn, supabase } from '@/lib/supabase'
import { notificationDb } from '@/lib/notificationDb'
import { errorMessage, hasSqlState } from '@/lib/errorMessage'
import {
  DELETE_TIMEOUT_MS,
  PING_ATTEMPTS,
  PING_TIMEOUT_MS,
  PUSH_PING,
  PUSH_PONG,
  READY_TIMEOUT_MS,
  SUBSCRIBE_TIMEOUT_MS,
  UNSUBSCRIBE_TIMEOUT_MS,
  UPDATE_WAITING_MESSAGE,
  alertSupport,
  deviceUserAgent,
  sameKey,
  urlBase64ToUint8Array,
} from '@/lib/pushSupport'

/** One of this person's saved devices, as the list shows it. The device's keys are never selected. */
export interface PushDeviceRow {
  id: string
  endpoint: string
  user_agent: string | null
  saved_at: string
  last_success_at: string | null
}

/** Dispatched on window after Turn on, Turn off and Remove, so every mounted device list reloads. */
export const PUSH_DEVICES_CHANGED_EVENT = 'rizeos:push-devices-changed'

// The sentences. A toast shows `error.message`, so each is something a realtor can read. The ones the
// Push alerts card also shows itself (to tell one apart, or for a failure that comes with no words) are
// exported, so the card keeps no copy of them (Task 8's review).
export const NOT_SET_UP = "Alerts aren't set up on the server yet."
const NOT_READY = "The app isn't ready for alerts yet. Reload the page and try again."
const NOT_A_PUSH_SERVICE = "This browser's alert service isn't supported. Try Chrome, Edge, Firefox or Safari."
// In the words the app already uses: dealParties.ts for a step-up (an AAL2 refusal), the session sentence
// of the edge-function callers (Commissions, Settings) for a missing session.
const STEP_UP = 'Confirm your sign-in with your authenticator app, then try again.'
const SESSION_GONE = 'Your session has expired. Please sign in again.'
// Also the card's spoken word for where an action left it off (Task 8's second review).
export const NOT_ON_HERE = 'Alerts are not on for this device.'
export const TEST_FAILED = "The test alert didn't send. Turn alerts off and on, then try again."
const TEST_UNREACHABLE = "The test alert didn't send. Check your connection, then try again."
// What the browser said when it refused is never shown: it is the cause.
const RESET_FAILED = "Couldn't reset this browser's alerts. Reload the page and try again."
const SUBSCRIBE_FAILED = "This browser couldn't turn on alerts. Check your connection and try again."
const LOOKUP_FAILED = "This browser wouldn't let Rize OS check its alerts. Reload the page and try again."
export const TURN_OFF_FAILED = "Couldn't turn alerts off on this device. Try again."
// What saving the device and removing one say when the database's refusal has no words of its own.
export const TURN_ON_FAILED = "Couldn't turn alerts on."
export const REMOVE_FAILED = "Couldn't remove that device."

/** The network down, for the thing the person was doing. */
const unreachable = (what: string): string => `Couldn't reach Rize OS to ${what}. Check your connection and try again.`

/** The key could not be fetched, for any reason but the server's own "not set up": a try-again sentence. */
export const SET_UP_UNREACHABLE = unreachable('set up alerts')

/** The cause a limit leaves behind when it is hit: the call it waited for, and how long. Never shown to a person. */
const timedOut = (call: string, ms: number): Error => new Error(`${call} did not settle within ${ms} ms`)

/** The part of a supabase-js error these functions read; a PostgrestError has all three. */
interface DatabaseError {
  code?: string
  message?: string
}

/**
 * Whether the answer to a supabase-js request did not come from the database. A PostgREST or Postgres error
 * ALWAYS has a code (a SQLSTATE or a PGRST code), so an error with NONE did not come from the database, and
 * its words are not for a person. Either the request never got an answer, and the words are the browser's own
 * ("TypeError: Failed to fetch" in Chrome, "TypeError: Load failed" in Safari, "TypeError: NetworkError when
 * attempting to fetch resource." in Firefox); or something else answered in the database's place, and
 * postgrest-js hands what it said over whole as the message, with no code: a gateway's HTML 502, Cloudflare's
 * "error code: 522", a proxy's plain text, Kong's "Invalid API key", a hotel Wi-Fi page that answered 200.
 * Every one of them is the connection to the person, as bindingDateErrorText (src/lib/bindingDate.ts) has it.
 */
function isUnreached(error: DatabaseError): boolean {
  return typeof error.code !== 'string' || error.code.trim() === ''
}

/**
 * A database error as the Error to throw: one that did not come from the database as a sentence for what was
 * being done, anything else in the database's own words (or `fallback` when it has none), the original always
 * the cause.
 */
function databaseFailure(error: DatabaseError, fallback: string, what: string): Error {
  if (isUnreached(error)) return new Error(unreachable(what), { cause: error })
  return new Error(errorMessage(error, fallback), { cause: error })
}

/** What a bounded wait came to: the value, or the limit. */
type Settled<T> = { settled: true; value: T } | { settled: false }

/**
 * `work`, or nothing if it has not settled within `ms`. The timer is always cleared, so one that is not
 * needed any more cannot fire later into nothing. A rejection of `work` within the limit is thrown.
 */
async function settledWithin<T>(work: PromiseLike<T>, ms: number): Promise<Settled<T>> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const late = new Promise<{ settled: false }>((resolve) => {
    timer = setTimeout(() => resolve({ settled: false }), ms)
  })
  try {
    return await Promise.race([Promise.resolve(work).then((value) => ({ settled: true as const, value })), late])
  } finally {
    clearTimeout(timer)
  }
}

// -- the VAPID key ------------------------------------------------------------------------------------------

/** The key in a successful answer, or null when there is none that can be used: not JSON, no publicKey, blank, or not base64. */
async function usableKey(response: Response): Promise<string | null> {
  try {
    const body: unknown = await response.json()
    const key = typeof body === 'object' && body !== null ? (body as { publicKey?: unknown }).publicKey : undefined
    if (typeof key !== 'string' || key.trim() === '') return null
    // throws on anything that is not base64, so a garbled key is "not set up", not a failure inside subscribe()
    urlBase64ToUint8Array(key.trim())
    return key.trim()
  } catch (err) {
    console.warn('push-alerts: could not read the key answer:', errorMessage(err))
    return null
  }
}

/**
 * The server's VAPID public key (GET push-alerts/key). Settings asks for it when the card mounts, so
 * Turn on does not wait on the network inside the click. Only the server's own verdict says it is not set
 * up: a 503 (no keys yet, until the release sets them) or a success without a usable key. Any other
 * status, and a network that is down, say nothing about the server's keys and are a try-again sentence;
 * the status is logged either way.
 */
export async function fetchVapidKey(): Promise<string> {
  let response: Response
  try {
    response = await fetch(edgeFn('push-alerts/key'))
  } catch (err) {
    console.warn('push-alerts: could not reach GET /key:', errorMessage(err))
    throw new Error(SET_UP_UNREACHABLE, { cause: err })
  }
  if (response.status === 503) {
    console.warn('push-alerts: GET /key answered 503 (the server has no VAPID keys yet)')
    throw new Error(NOT_SET_UP)
  }
  if (!response.ok) {
    console.warn(`push-alerts: GET /key answered ${response.status}`)
    throw new Error(SET_UP_UNREACHABLE)
  }
  const key = await usableKey(response)
  if (key === null) {
    console.warn(`push-alerts: GET /key answered ${response.status} without a usable key`)
    throw new Error(NOT_SET_UP)
  }
  return key
}

// -- this browser's registration, subscription and worker --------------------------------------------------

/** The registration that exists. Answers at once: with nothing when there is none, which is the case a page with no service worker is in. */
async function registrationOf(): Promise<ServiceWorkerRegistration | undefined> {
  try {
    return await navigator.serviceWorker?.getRegistration()
  } catch (err) {
    throw new Error(LOOKUP_FAILED, { cause: err })
  }
}

/** The registration's push subscription, or null. */
async function subscriptionOf(registration: ServiceWorkerRegistration): Promise<PushSubscription | null> {
  try {
    return await registration.pushManager.getSubscription()
  } catch (err) {
    throw new Error(LOOKUP_FAILED, { cause: err })
  }
}

/** This browser's push subscription, or null. Answers at once. */
async function currentSubscription(): Promise<PushSubscription | null> {
  const registration = await registrationOf()
  // pushManager is missing on a browser that cannot push (an iPhone outside the Home Screen has a worker and no PushManager)
  if (!registration?.pushManager) return null
  return subscriptionOf(registration)
}

/** serviceWorker.ready, but a page whose worker never comes up says so after READY_TIMEOUT_MS instead of waiting for ever. */
async function readyRegistration(): Promise<ServiceWorkerRegistration> {
  const container = navigator.serviceWorker
  if (!container) throw new Error(NOT_READY)
  let timer: ReturnType<typeof setTimeout> | undefined
  const late = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(NOT_READY)), READY_TIMEOUT_MS)
  })
  try {
    return await Promise.race([container.ready, late])
  } finally {
    clearTimeout(timer)
  }
}

function isPong(data: unknown): boolean {
  return typeof data === 'object' && data !== null && (data as { type?: unknown }).type === PUSH_PONG
}

/**
 * One ping: true when the worker's pong comes within PING_TIMEOUT_MS, false on silence and false at once for
 * a worker that cannot be messaged. The worker answers on the page's service-worker message channel, not on a
 * port, so the answer is a 'message' event on navigator.serviceWorker. The listener and the timer are always
 * taken off again.
 */
function pingOnce(container: ServiceWorkerContainer, worker: ServiceWorker): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    const onMessage = (event: MessageEvent): void => {
      if (isPong(event.data)) done(true)
    }
    // armed before `done` is written, run only after it is: the wait for the answer ends in "no"
    const timer = setTimeout(() => done(false), PING_TIMEOUT_MS)
    const done = (answered: boolean): void => {
      clearTimeout(timer)
      container.removeEventListener('message', onMessage)
      resolve(answered)
    }
    container.addEventListener('message', onMessage)
    try {
      worker.postMessage({ type: PUSH_PING })
    } catch (err) {
      console.warn('push: could not message the service worker:', errorMessage(err))
      done(false)
    }
  })
}

/**
 * Whether the ACTIVE service worker can show a push alert: it answers the ping (public/push-sw.js) within
 * PING_TIMEOUT_MS, on either of PING_ATTEMPTS tries (a worker the browser had stopped is woken by the first
 * and answers the second; a pong that comes late to the first is heard by the second's listener). `registration`
 * is the one Turn on already waited for; without it the registration that exists is asked. No worker, a worker
 * that cannot be messaged, and silence through every try are all "no".
 */
export async function swSupportsPush(registration?: ServiceWorkerRegistration): Promise<boolean> {
  const container = navigator.serviceWorker
  if (!container) return false
  const worker = (registration ?? await registrationOf())?.active
  if (!worker) return false
  for (let attempt = 0; attempt < PING_ATTEMPTS; attempt++) {
    if (await pingOnce(container, worker)) return true
  }
  return false
}

/** The address of this browser's push subscription, or null when it has none. */
export async function currentEndpoint(): Promise<string | null> {
  return (await currentSubscription())?.endpoint ?? null
}

/**
 * On for this device: the browser can push, permission is granted, it holds a subscription made with THIS
 * server's key (one made under another environment, or before the key rotated, can never receive its
 * alerts), and that subscription is saved for the signed-in person. Another person's device (an office
 * iPad someone else turned on) is not this person's row to read, so it is off for them.
 */
export async function alertsOnHere(key: string): Promise<boolean> {
  if (alertSupport() !== 'supported' || Notification.permission !== 'granted') return false
  const subscription = await currentSubscription()
  if (!subscription || !sameKey(subscription.options.applicationServerKey, key)) return false
  const { data, error } = await notificationDb
    .from('push_subscriptions')
    .select('id')
    .eq('endpoint', subscription.endpoint)
    .maybeSingle()
  if (error) throw databaseFailure(error, "Couldn't check this device.", 'check this device')
  return data !== null
}

// -- Turn on ----------------------------------------------------------------------------------------------

/** save_push_subscription's refusals in words a person can act on; the network's failure and anything unforeseen go through databaseFailure. */
function saveFailure(error: DatabaseError): Error {
  // 22023: the browser's push service is not one the server sends to
  if (hasSqlState(error, '22023')) return new Error(NOT_A_PUSH_SERVICE, { cause: error })
  // 42501 is raised twice by the function: a password-only session of an MFA-enrolled member (mfa_ok()), and no
  // session ("not signed in"). A person with no session never gets that far: the function is not granted to
  // anon, so the refusal is the permission check's, in the words below, before the function runs. PGRST303 is
  // PostgREST's own refusal of an expired token. All three are a session that is gone.
  if (hasSqlState(error, '42501') && /MFA required/i.test(error.message ?? '')) return new Error(STEP_UP, { cause: error })
  const signedOut = (hasSqlState(error, '42501') && /not signed in|permission denied for function save_push_subscription/i.test(error.message ?? ''))
    || error.code === 'PGRST303'
  if (signedOut) return new Error(SESSION_GONE, { cause: error })
  // anything else is not guessed at
  return databaseFailure(error, TURN_ON_FAILED, 'save this device')
}

/**
 * Turn on for this device. `key` is the server's VAPID public key, fetched earlier (fetchVapidKey). Returns
 * 'denied' when the person (or the browser) refused permission and 'dismissed' when the prompt was closed
 * without a choice, which is not a refusal. Throws UPDATE_WAITING_MESSAGE when the active worker cannot show
 * the alert, and an Error with a sentence for anything else that goes wrong.
 */
export async function enableAlerts(key: string): Promise<'on' | 'denied' | 'dismissed'> {
  const permission = await Notification.requestPermission()
  if (permission === 'denied') return 'denied'
  if (permission !== 'granted') return 'dismissed'

  const { data: { session } } = await supabase.auth.getSession()
  if (!session) throw new Error(SESSION_GONE)

  const registration = await readyRegistration()
  if (!(await swSupportsPush(registration))) throw new Error(UPDATE_WAITING_MESSAGE)

  let subscription = await subscriptionOf(registration)
  if (subscription && !sameKey(subscription.options.applicationServerKey, key)) {
    let reset: Settled<boolean>
    try {
      reset = await settledWithin(subscription.unsubscribe(), UNSUBSCRIBE_TIMEOUT_MS)
    } catch (err) {
      throw new Error(RESET_FAILED, { cause: err })
    }
    // a subscription that may still be live is never joined by a second one
    if (!reset.settled) throw new Error(RESET_FAILED, { cause: timedOut('unsubscribe()', UNSUBSCRIBE_TIMEOUT_MS) })
    subscription = null
  }
  if (!subscription) {
    // Counted from here, after the permission answer: the prompt is the person's and is never timed.
    let made: Settled<PushSubscription>
    try {
      made = await settledWithin(registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key),
      }), SUBSCRIBE_TIMEOUT_MS)
    } catch (err) {
      throw new Error(SUBSCRIBE_FAILED, { cause: err })
    }
    if (!made.settled) throw new Error(SUBSCRIBE_FAILED, { cause: timedOut('pushManager.subscribe()', SUBSCRIBE_TIMEOUT_MS) })
    subscription = made.value
  }

  const { endpoint, keys } = subscription.toJSON()
  // a push subscription always has both keys; one that does not is not a browser we can send to
  if (!keys?.p256dh || !keys.auth) throw new Error(NOT_A_PUSH_SERVICE)
  const { error } = await notificationDb.rpc('save_push_subscription', {
    p_endpoint: endpoint ?? subscription.endpoint,
    p_p256dh: keys.p256dh,
    p_auth: keys.auth,
    p_user_agent: deviceUserAgent(),
  })
  // The subscription stays on failure: it is the person's, and the retry reuses it.
  if (error) throw saveFailure(error)
  return 'on'
}

// -- Turn off ---------------------------------------------------------------------------------------------

/**
 * Turn off for this device: unsubscribe the browser FIRST, waiting UNSUBSCRIBE_TIMEOUT_MS for that, then
 * delete this person's row for its address, waiting DELETE_TIMEOUT_MS for that and no longer (see the header:
 * a delete that has not answered is logged and let go, not a failure). A device that cannot be unsubscribed,
 * or whose unsubscribe has not settled within its limit, still has its row deleted (so nothing is sent to it
 * after the person signs out), and the failure is then thrown; when the delete fails as well, one error
 * carries both. A delete that removes nothing is not a failure. Settles at once when there is no service
 * worker, no registration or no subscription.
 */
export async function disableAlerts(): Promise<void> {
  const subscription = await currentSubscription()
  if (!subscription) return
  const endpoint = subscription.endpoint
  let unsubscribeFailure: unknown = null
  try {
    const unsubscribed = await settledWithin(subscription.unsubscribe(), UNSUBSCRIBE_TIMEOUT_MS)
    if (!unsubscribed.settled) unsubscribeFailure = timedOut('unsubscribe()', UNSUBSCRIBE_TIMEOUT_MS)
  } catch (err) {
    unsubscribeFailure = err
  }
  const deleted = await settledWithin(
    notificationDb.from('push_subscriptions').delete().eq('endpoint', endpoint),
    DELETE_TIMEOUT_MS,
  )
  let deleteFailure: DatabaseError | null = null
  if (!deleted.settled) {
    console.warn('push: the device row was not deleted within the limit; the push service drops it at the next alert')
  } else {
    deleteFailure = deleted.value.error
  }
  if (unsubscribeFailure !== null && deleteFailure !== null) {
    throw new Error(TURN_OFF_FAILED, { cause: new AggregateError([unsubscribeFailure, deleteFailure], 'the unsubscribe and the delete both failed') })
  }
  if (unsubscribeFailure !== null) throw new Error(TURN_OFF_FAILED, { cause: unsubscribeFailure })
  if (deleteFailure !== null) throw databaseFailure(deleteFailure, TURN_OFF_FAILED, 'turn alerts off')
}

/**
 * After a sign-out from anywhere (another tab, an expired session): unsubscribe this browser, waiting
 * UNSUBSCRIBE_TIMEOUT_MS for that. There is no session left to delete the row with, so the server drops it
 * at the next alert (the push service answers 404 or 410 for a subscription that is gone). Never touches the
 * database and makes no request; settles at once when there is nothing to unsubscribe.
 */
export async function forgetThisDevice(): Promise<void> {
  const subscription = await currentSubscription()
  if (!subscription) return
  let unsubscribed: Settled<boolean>
  try {
    unsubscribed = await settledWithin(subscription.unsubscribe(), UNSUBSCRIBE_TIMEOUT_MS)
  } catch (err) {
    throw new Error(TURN_OFF_FAILED, { cause: err })
  }
  if (!unsubscribed.settled) throw new Error(TURN_OFF_FAILED, { cause: timedOut('unsubscribe()', UNSUBSCRIBE_TIMEOUT_MS) })
}

// -- the device list --------------------------------------------------------------------------------------

/** This person's saved devices, newest first. Own-row RLS, so no person filter; the keys are not selected. Zero rows is a list, not an error. */
export async function listMyDevices(): Promise<PushDeviceRow[]> {
  const { data, error } = await notificationDb
    .from('push_subscriptions')
    .select('id, endpoint, user_agent, saved_at, last_success_at')
    .order('saved_at', { ascending: false })
  if (error) throw databaseFailure(error, "Couldn't load your devices.", 'load your devices')
  return data ?? []
}

/** Remove one of this person's devices (own-row delete). A delete that removes nothing is not a failure. */
export async function removeDevice(id: string): Promise<void> {
  const { error } = await notificationDb.from('push_subscriptions').delete().eq('id', id)
  if (error) throw databaseFailure(error, REMOVE_FAILED, 'remove that device')
}

// -- the test alert ---------------------------------------------------------------------------------------

/** The sentence for a test alert the server refused: its own when it has one (push-alerts answers every status with { error }). */
async function testFailure(response: Response): Promise<string> {
  if (response.status === 401) return SESSION_GONE
  const body: unknown = await response.json().catch(() => null)
  const said = typeof body === 'object' && body !== null ? (body as { error?: unknown }).error : undefined
  return typeof said === 'string' && said.trim() !== '' ? said.trim() : TEST_FAILED
}

/**
 * Send the test alert to this device (POST push-alerts/test with the person's session). The server may take
 * up to a minute: a brand-new subscription is refused by some push services for about ten seconds, so it
 * retries. Resolves when the push service took it; otherwise throws the server's own sentence.
 */
export async function sendTestAlert(): Promise<void> {
  const endpoint = await currentEndpoint()
  if (endpoint === null) throw new Error(NOT_ON_HERE)
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) throw new Error(SESSION_GONE)
  let response: Response
  try {
    response = await fetch(edgeFn('push-alerts/test'), {
      method: 'POST',
      headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint }),
    })
  } catch (err) {
    console.warn('push-alerts: the test alert request failed:', errorMessage(err))
    throw new Error(TEST_UNREACHABLE, { cause: err })
  }
  if (!response.ok) throw new Error(await testFailure(response))
}
